import { useEffect, useRef, useState } from 'react';

/* ==================================================================
   useInView
   ------------------------------------------------------------------
   Reveal trigger. Fires once by default and then disconnects, so a
   revealed section costs nothing for the rest of the session.
   Falls back to "visible" when IntersectionObserver is unavailable,
   which means content is never trapped behind a missing API.
   ================================================================== */

export default function useInView(options) {
  const {
    threshold = 0.18,
    rootMargin = '0px 0px -8% 0px',
    once = true,
    skip = false,
  } = options || {};

  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (skip) {
      setInView(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once, skip]);

  return [ref, inView];
}
